import { Option } from "fp";
import { useRouteMatch } from "react-router-dom";

import { ID, Reference, Store } from "~/Data";
import * as Driver from "~/Driver";
import * as IndividualDriver from "~/IndividualDriver";
import * as OptionalNonEmptyString from "~/OptionalNonEmptyString";
import { Route as GenericRoute } from "~/Route";
import { Time } from "~/Time";

import * as Active from "./Active";
import * as Assets from "./Assets";
import * as Event from "./Event";
import * as Signature from "./Signature";

export * from "./Component";
export * as Action from "./Action";
export * as Active from "./Active";
export * as Assets from "./Assets";
export * as Audit from "./Audit";
export * as Data from "./Data";
export { DriverTimezoneMismatchWarning } from "./DriverTimezoneMismatchWarning";
export * from "./DVIR";
export * as Chart from "./Chart";
export * as Event from "./Event";
export { ExportMenu } from "./ExportMenu";
export * as Form from "./Form";
export * as Issue from "./Issue";
export * as Request from "./Request";
export * as Response from "./Response";
export * as Revision from "./Revision";
export * as Signature from "./Signature";
export * as Suggestion from "./Suggestion";
export * from "./Summary";
export * as Unassigned from "./Unassigned";
export * as Violation from "./Violation";
export * as Table from "./Table";
export * as Timeline from "./Timeline";

export type Log = Identity & {
  readonly driver: { readonly name?: Driver.Name.Name };
  readonly coDriver?: OptionalNonEmptyString.OptionalNonEmptyString;
  readonly distance: number;
  readonly documents?: OptionalNonEmptyString.OptionalNonEmptyString;
  readonly signature?: Signature.Signature;
  readonly assets?: Assets.Assets;
  readonly events: readonly Event.Event[];
};

/** A `Log` is unique for combinations of `dateTime` and `driver` */
export type Identity = Time.DateTime & {
  readonly driver: Reference.Reference;
};

export const equals = (a: Identity) => (b: Identity): boolean =>
  id(a) === id(b);

export const id = ({ driver, dateTime }: Identity): ID.ID =>
  `${driver.id}${dateTime}`;

export type Route = Time.DateTime & {
  readonly driver: Driver.Driver;
};

export type RouteReference = {
  readonly route: Route;
};

export namespace Route {
  export const definition = () => `${Driver.Page.Route.definition()}/:date`;
  export const href = (route: Route) =>
    `${Driver.Page.Route.href(route)}/${Time.DateTime.toISOWithoutTime(route)}`;

  export const use = (route: Route) => GenericRoute.use(href(route));
  export const useData = (): Option<Route> => {
    const reference = Option.toUndefined(Driver.useReferenceFromRoute());
    const driver = IndividualDriver.useFromIndividualReference<Driver.Driver | undefined>({
      ...reference,
      missing: () => reference,
      found: (driver) => driver,
    });

    const dateTime = pipe(
      useRouteMatch<{ readonly date: string }>(definition()),
      Option.fromNullable,
      Option.map(({ params: { date } }) => date),
      Option.chain(flow(Time.DateTime.fromString, Option.fromEither))
    );

    return pipe(
      Option.fromNullable(driver),
      Option.chain((driver) =>
        pipe(
          dateTime,
          Option.map((dateTime) => ({ driver, ...dateTime }))
        )
      )
    );
  };
}

export namespace State {
  export type State = {
    readonly active: Active.State.State;
    readonly store: Store.Store<Log>;
  };

  export const initial = (): State => ({
    active: Active.State.initial(),
    store: Store.initial(),
  });

  export namespace Log {
    export type State = Log | undefined;
    export const initial = (): State => undefined;
  }
}
